'use client'

import { cn } from '../../utils/cn'

export interface ProgressStep {
  id: string
  label: string
  description?: string
  status: 'pending' | 'active' | 'completed'
}

export interface StepsWithProgressProps {
  steps: ProgressStep[]
  title?: string
  helpText?: string
  className?: string
  onStepClick?: (id: string) => void
}

export function StepsWithProgress({ steps, title, helpText, className, onStepClick }: StepsWithProgressProps) {
  const completed = steps.filter(s => s.status === 'completed').length
  const progress = steps.length > 0 ? (completed / steps.length) * 100 : 0

  return (
    <div className={cn('rounded-xl border border-border bg-card', className)}>
      {/* Header + progress bar */}
      <div className="px-5 py-4 border-b border-border">
        {title && <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-border overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-xs font-mono text-muted-foreground">{completed}/{steps.length}</span>
        </div>
      </div>

      {/* Steps list */}
      <div className="divide-y divide-border">
        {steps.map((step, i) => (
          <button
            key={step.id}
            onClick={() => onStepClick?.(step.id)}
            disabled={!onStepClick}
            className="flex items-center gap-3 w-full px-5 py-3 text-left hover:bg-muted/20 transition-colors"
          >
            <div className={cn(
              'flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0',
              step.status === 'completed' && 'bg-primary/20 text-primary',
              step.status === 'active' && 'bg-primary text-primary-foreground',
              step.status === 'pending' && 'bg-muted text-muted-foreground',
            )}>
              {step.status === 'completed' ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
              ) : i + 1}
            </div>
            <div>
              <span className={cn('text-sm font-medium', step.status === 'pending' ? 'text-muted-foreground' : 'text-foreground')}>{step.label}</span>
              {step.description && <p className="text-xs text-muted-foreground">{step.description}</p>}
            </div>
          </button>
        ))}
      </div>

      {/* Help footer */}
      {helpText && (
        <div className="px-5 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">{helpText}</p>
        </div>
      )}
    </div>
  )
}
